import { FormEvent, useCallback, useContext, useMemo, useRef } from 'react';

import {
  AutocompleteApi,
  AutocompleteState,
  createAutocomplete,
} from '@algolia/autocomplete-core';
import type { SearchOptions } from '@algolia/client-search';
import { CookieContext } from '@imgly/imgly-website-components';
import { navigate } from 'gatsby';
import { groupBy } from 'lodash';
import queryString from 'query-string';

import { DocSearchHit, IndexTypes } from '../../../types';
import { getUnifiedCodeLanguageId } from '../../../utils/getUnifiedCodeLanguageId';
import { sanitizeSlug } from '../../../utils/sanitizeSlug';
import { ContentContext } from '../../context/ContentContext';

import { createStoredSearches, StoredSearchPlugin } from './stored-searches';
import { useSearchClient } from './useSearchClient';

export const useAutoComplete = (
  appId: string,
  apiKey: string,
  indexName: string,
  searchParameters: SearchOptions,
  onClose: () => void,
  initialQuery: string,
  placeholder: string,
  snippetLength: React.MutableRefObject<number>,
  setState: (state: AutocompleteState<DocSearchHit>) => void,
): {
  autocomplete: AutocompleteApi<
    DocSearchHit,
    FormEvent<HTMLFormElement>,
    MouseEvent,
    KeyboardEvent
  >;
  favoriteSearches: StoredSearchPlugin;
  recentSearches: StoredSearchPlugin;
  saveRecentSearch: (item: DocSearchHit) => void;
} => {
  const { activeItems } = useContext(ContentContext);
  const { trackFunctional } = useContext(CookieContext);
  const { handleSettingsClick } = useContext(ContentContext);

  const favoriteSearches = useRef(
    createStoredSearches({
      key: `__DOCSEARCH_FAVORITE_SEARCHES__${indexName}`,
      trackFunctional,
      limit: 10,
    }),
  ).current;

  const recentSearches = useRef(
    createStoredSearches({
      key: `__DOCSEARCH_RECENT_SEARCHES__${indexName}`,
      trackFunctional,
      // We display 7 recent searches and there's no favorites, but only
      // 4 when there are favorites.
      limit: favoriteSearches.getAll().length === 0 ? 7 : 4,
    }),
  ).current;

  const saveRecentSearch = useCallback(
    (item: DocSearchHit) => {
      // We don't store Text record, but their parent.
      const search =
        item.type === IndexTypes.TEXT || item.type === IndexTypes.CODE
          ? {
              ...item,
              type: IndexTypes.PAGE,
              content: item.name,
              anchor: '',
              query: '',
            }
          : item;

      // We save the recent search only if it's not favorited.
      if (
        search &&
        favoriteSearches
          .getAll()
          .findIndex(x => x.objectID === search.objectID) === -1
      ) {
        recentSearches.add(search);
      }
    },
    [favoriteSearches, recentSearches],
  );

  const searchClient = useSearchClient(appId, apiKey);

  const autocomplete = useMemo(
    () =>
      createAutocomplete<
        DocSearchHit,
        FormEvent<HTMLFormElement>,
        MouseEvent,
        KeyboardEvent
      >({
        id: 'docsearch',
        defaultActiveItemId: 0,
        placeholder,
        openOnFocus: true,
        initialState: {
          query: initialQuery,
          context: {
            searchSuggestions: [],
          },
        },
        onStateChange(props) {
          setState(props.state);
        },
        getSources({ query, state: sourcesState, setContext, setStatus }) {
          // if no query is used we display recent and favorit results
          // these values are local
          if (!query) {
            return [
              // get recent search items
              {
                sourceId: 'recentSearches',
                onSelect({ item, event }) {
                  saveRecentSearch(item);

                  if (!event.shiftKey && !event.ctrlKey && !event.metaKey) {
                    onClose();
                  }
                },
                getItemUrl({ item }) {
                  // note: this url will be used by the Autocomplete keyboard events
                  return sanitizeSlug(`${item.slug}${item.anchor}`);
                },
                getItems() {
                  return recentSearches.getAll();
                },
              },
              // get favorite search items
              {
                sourceId: 'favoriteSearches',
                onSelect({ item, event }) {
                  saveRecentSearch(item);

                  if (!event.shiftKey && !event.ctrlKey && !event.metaKey) {
                    onClose();
                  }
                },
                getItemUrl({ item }) {
                  // note: this url will be used by the Autocomplete keyboard events
                  return sanitizeSlug(`${item.slug}${item.anchor}`);
                },
                getItems() {
                  return favoriteSearches.getAll();
                },
              },
            ];
          }

          // use Algolia search client if a query is used
          return searchClient
            .search<DocSearchHit>([
              {
                query,
                indexName,
                params: {
                  attributesToRetrieve: [
                    'type',
                    'name',
                    'content',
                    'slug',
                    'anchor',
                    'query',
                  ],
                  attributesToSnippet: [`content:${snippetLength.current}`],
                  snippetEllipsisText: '…',
                  highlightPreTag: '<mark>',
                  highlightPostTag: '</mark>',
                  hitsPerPage: 20,
                  ...searchParameters,
                },
              },
            ])
            .catch(error => {
              // The Algolia `RetryError` happens when all the servers have
              // failed, meaning that there's no chance the response comes
              // back. This is the right time to display an error.
              // See https://github.com/algolia/algoliasearch-client-javascript/blob/2ffddf59bc765cd1b664ee0346b28f00229d6e12/packages/transporter/src/errors/createRetryError.ts#L5
              if (error.name === 'RetryError') {
                setStatus('error');
              }

              throw error;
            })
            .then(({ results }) => {
              const { hits, nbHits } = results[0];

              // prepare the hits by filtering out non-unique query values based on the page settings
              const sources = hits.map(hit => {
                const temp = hit;

                if (temp.query) {
                  temp.query = temp.query
                    .filter(
                      queryItem =>
                        !Object.values(activeItems).find(
                          item =>
                            getUnifiedCodeLanguageId(item) ===
                            getUnifiedCodeLanguageId(queryItem),
                        ),
                    )
                    // sort alphabetically to make grouping easier
                    .sort((a, b) => {
                      if (a === b) {
                        return 0;
                      }
                      return a < b ? -1 : 1;
                    });
                }

                return temp;
              });

              // We store the `lvl0`s to display them as search suggestions
              // in the "no results" screen.
              if (
                (sourcesState.context.searchSuggestions as any[]).length <
                Object.keys(sources).length
              ) {
                setContext({
                  searchSuggestions: Object.keys(sources),
                });
              }

              setContext({ nbHits });

              // group the hits by query and slug
              const groupedSources = Object.values(
                groupBy(sources, source => `${source.query}+${source.slug}`),
              );

              // sort the collections by importance
              // 1. results that match the search the best
              // 2. results that share the most settings with current page
              // 3. results that shate the least settings with current page
              // 4. general results that don't have settings
              const sortedSources = groupedSources.sort((a, b) => {
                const aQuery = a[0].query;
                const bQuery = b[0].query;

                // move groups without a query value to the beginning of the list
                if (!aQuery) {
                  return -1;
                }
                if (!bQuery) {
                  return 1;
                }

                // move general groups to the end of the list
                if (aQuery[0] === 'none') {
                  return 1;
                }
                if (bQuery[0] === 'none') {
                  return -1;
                }

                // move values with least amount of queries before
                if (aQuery.length < bQuery.length) {
                  return -1;
                }
                if (aQuery.length > bQuery.length) {
                  return 1;
                }

                return 0;
              });

              // create the collection entries
              return sortedSources
                .map(group => group.slice(0, 5))
                .map((items, index) => {
                  return {
                    sourceId: `hits${index}`,
                    onSelect({ item, event }) {
                      saveRecentSearch(item);

                      if (!event.shiftKey && !event.ctrlKey && !event.metaKey) {
                        onClose();
                      }
                    },
                    getItemUrl({ item }) {
                      // note: this url will be used by the Autocomplete keyboard events
                      return sanitizeSlug(`${item.slug}${item.anchor}`);
                    },
                    getItems() {
                      return items;
                    },
                  };
                });
            });
        },
        navigator: {
          navigate({ itemUrl }) {
            // update the session storage of the content manager
            // if the route contains query params
            if (itemUrl.includes('?')) {
              const params = queryString.parse(
                itemUrl.replace(/#.*(?=\?)|#.*(?=$)/, '').split('?')[1],
              );
              const values: string[] = Object.values(params).filter(
                (value): value is string => typeof value === 'string',
              );

              handleSettingsClick(values);
            }

            // note: Use internal Gatsby navigation if a result is selected with enter
            // https://www.algolia.com/doc/ui-libraries/autocomplete/core-concepts/keyboard-navigation/
            navigate(itemUrl);
          },
        },
      }),
    [
      indexName,
      searchParameters,
      searchClient,
      onClose,
      recentSearches,
      favoriteSearches,
      saveRecentSearch,
      initialQuery,
      placeholder,
      navigator,
      activeItems,
    ],
  );

  return { autocomplete, favoriteSearches, recentSearches, saveRecentSearch };
};
